// src/prototype/components/EAPersonalizationWidget.tsx
// ============================================================
// PERSONALIZATION WIDGET — one percentage, on every journey surface
//
// ─────────────────────────────────────────────────────────────────────────────
// THE PERCENTAGE IS BACK, THE OLD FORMULA IS NOT.
//
// This widget briefly carried a two-line coverage summary. That was part of putting a
// diagnostic report where a status indicator belongs, and it came out with the panel.
//
// What it shows now is a single number derived from measure coverage by tier weight
// (lib/coverage/personalizationPercent), not the retired `0.4·profile + 0.5·surveys +
// 0.1·documents` composite. The difference that matters: documents used to be worth ten
// percent of the score, and are now worth the most of anything.
//
// This component holds NO number. The percentage and its band arrive as props, and the
// caller derives both from the same aggregates the rest of the app reads.
// ─────────────────────────────────────────────────────────────────────────────
// ============================================================

import { personalizationBand } from '../../lib/coverage/personalizationPercent';

interface EAPersonalizationWidgetProps {
  /** Whole number, 0 to 100. Derived by the caller; never computed here. */
  percent: number;
  /** Click-through to the intake surface. Optional. */
  onClick?: () => void;
}

export function EAPersonalizationWidget({ percent, onClick }: EAPersonalizationWidgetProps) {
  const band = personalizationBand(percent);

  const body = (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#18365E]">Personalization</span>
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${band.bgColor} ${band.color}`}
        >
          {band.label}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xl font-bold text-[#18365E] tabular-nums">{percent}%</span>
        <div className="flex-1 h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${band.barColor}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      <p className="text-[10px] text-[#717182] leading-snug mt-2">
        How strongly your inputs inform the scoring measures. Documents count for the most.
      </p>
    </>
  );

  return (
    <div className="bg-white rounded-xl shadow-md border border-[#e5e7eb] p-3 h-full flex flex-col">
      {onClick ? (
        <button type="button" onClick={onClick} className="w-full text-left" aria-label="Open your inputs">
          {body}
        </button>
      ) : (
        body
      )}
    </div>
  );
}

export default EAPersonalizationWidget;
